import React, { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { MapPinOff } from 'lucide-react';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { SpawnRow } from '../../components/world/SpawnRow';
import { EventChip } from '../../components/world/EventChip';
import { Disclosure, StatList } from '../../components/world/Disclosure';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { useHashScroll } from '../../hooks/useHashScroll';
import { MonsterItemDrops } from '../../components/world/MonsterItemDrops';
import { EventPositionList } from '../../components/world/EventPositionList';
import { NpcCard } from '../../components/progression/NpcCard';
import { npcs } from '../../data/guide/npcs';
import { monsterKind } from '../../utils/monsterKind';
import { useEventPositions } from '../../hooks/useEventPositions';
import { positionsFor } from '../../utils/eventPositions';
import { EntityImage } from '../../components/world/EntityImage';
import { imageEntryForMonster } from '../../utils/entityImages';

/** Posiciones del monstruo dentro de eventos (32_event_spawn_rules), separadas de los spots permanentes. */
function MonsterEventPositions({
  w,
  monsterId
}: {
  w: WorldData;
  monsterId: number;
}) {
  const ep = useEventPositions();
  const list = ep ? positionsFor(ep.positions, monsterId) : [];
  if (!list.length) return null;
  return <WorldSection id="posiciones-evento" title="Posiciones dentro de eventos" count={list.length} data-id="element-2792" data-mp="1geziqw0ji03p">
      <EventPositionList w={w} positions={list} max={12} data-id="element-2793" data-mp="1le2mmd4sa0a3" />
    </WorldSection>;
}
import { BoxContents } from '../../components/world/BagSourceList';
import { monsterBags } from '../../utils/bagSources';
import { mapName, mapPath, formatSeconds, monsterPath } from '../../utils/worldFormat';
import { CanonicalPath } from '../../components/world/CanonicalPath';
import { NpcEntityView } from '../../components/world/NpcEntityView';
import { monsterIdFromPrettyPath } from '../../utils/prettyRoutes';
import type { Monster, WorldData } from '../../types/world';
export function MonsterDetail() {
  const {
    monsterId,
    slug
  } = useParams();
  const {
    pathname
  } = useLocation();
  useHashScroll();
  return <WorldGate data-id="element-2794" data-mp="15robiyb5jop0">
      {w => {
      const id = slug ? monsterIdFromPrettyPath(pathname) : Number(monsterId);
      const m = id === undefined ? undefined : w.monsters.get(id);
      if (!m) {
        return <div className="rounded-2xl border border-white/10 p-6" data-id="element-2795" data-mp="e21pq2xj1ryv">
              <p className="text-white" data-id="element-2796" data-mp="rsycve8opgyj">No encontramos ese monstruo en los archivos.</p>
              <Link to="/guia/mapas?vista=monstruos" className="mt-3 inline-flex font-semibold text-[#D4AF37]" data-id="element-2797" data-mp="n1omgbfba9ll">Volver a Monstruos</Link>
            </div>;
      }
      return <>
            <CanonicalPath to={monsterPath(m.id)} data-id="element-2798" data-mp="3qiop2wqg1s1y" />
            {monsterKind(w, m) === 'npc' ? <NpcEntityView w={w} m={m} data-id="element-2799" data-mp="3qgklyh0dcx2q" /> : <MonsterView w={w} m={m} data-id="element-2800" data-mp="2604gpirq9yvm" />}
          </>;
    }}
    </WorldGate>;
}

/** Procedencia de la foto, separada de la imagen y de los datos de juego. */
function PhotoProvenance({
  w,
  monsterId
}: {
  w: WorldData;
  monsterId: number;
}) {
  const img = imageEntryForMonster(w, monsterId)?.image;
  if (!img || img.origin !== 'paquete') return null;
  const family = img.familyReference?.sharedWith ?? [];
  return <Disclosure title="Procedencia de la imagen" data-id="element-2801" data-mp="2ppom0j8lwoyw">
      <p className="text-sm text-gray-300" data-id="element-2802" data-mp="2tpyq1qcu95b4">
        Foto de referencia visual{img.packName ? ` (paquete ${img.packName})` : ''}{img.sourceName ? `, rotulada «${img.sourceName}»` : ''}. Retocada para quitar la marca: puede tener
        pequeñas diferencias de textura. No es una captura del cliente Vanguard ni confirma drops, estadísticas ni ubicaciones.
      </p>
      {family.length > 0 && <p className="mt-2 text-sm text-gray-400" data-id="element-2803" data-mp="19a3wlx2b06kn">
          Referencia de familia: la misma foto se usa para los ID {family.join(', ')}. Cada ID conserva su propia ficha, mapas, drops y estadísticas.
        </p>}
      <p className="mt-2 text-xs text-gray-500" data-id="element-2804" data-mp="1jsihwjbcsvrd">{img.matchedBy}</p>
    </Disclosure>;
}
function MonsterView({
  w,
  m
}: {
  w: WorldData;
  m: Monster;
}) {
  useEffect(() => {
    document.title = `${m.name} · Monstruos · Guía MU Vanguard`;
  }, [m.name]);
  const locations = w.idx.locationsByMonster.get(m.id) ?? [];
  // Solo NPC con identidad confirmada (monsterId); los candidatos de Pet Trainer no reciben su tarjeta.
  const guideNpc = npcs.find(n => n.monsterId === m.id);
  const permanent = locations.filter(l => l.kind === 'ordinary');
  const temporary = locations.filter(l => l.kind !== 'ordinary');
  const spawns = w.idx.spawnsByMonster.get(m.id) ?? [];
  const spots = spawns.filter(s => s.kind === 'spot');
  const invasionSpawns = spawns.filter(s => s.kind === 'invasion');
  const eventIds = Array.from(new Set([...locations, ...spawns].map(x => x.eventId).filter((v): v is string => !!v)));
  return <div data-id="element-2805" data-mp="812pdq4fpngi">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Mapas y spots',
      to: '/guia/mapas'
    }, {
      label: 'Monstruos',
      to: '/guia/mapas?vista=monstruos'
    }, {
      label: m.name
    }]} eyebrow={guideNpc || monsterKind(w, m) === 'npc' ? 'NPC' : 'Monstruo'} title={m.name} media={imageEntryForMonster(w, m.id)?.image ? <EntityImage entry={imageEntryForMonster(w, m.id)} alt={m.name} name={m.name} size="hero" lazy={false} kind={guideNpc ? 'persona' : 'monstruo'} data-id="element-2807" data-mp="qqhpnicaq8ju" /> : undefined} data-id="element-2806" data-mp="81b31k8dswsr">
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-2808" data-mp="vrf5ho9u2qi6">
          {m.level !== undefined && <span className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1.5 text-sm font-semibold text-[#F3E5AB]" data-id="element-2809" data-mp="10mks6pb2897b">Nivel del monstruo (catálogo): {m.level}</span>}
          <CopyLinkButton label="Copiar enlace" data-id="element-2810" data-mp="32hv10sr1b22o" />
        </div>
      </WorldPageHeader>

      <div className="space-y-10" data-id="element-2811" data-mp="37f51d3fyoy5a">
        <WorldSection id="ubicaciones" title="Dónde aparece" data-id="element-2812" data-mp="2zdxdxdakcrrz">
          {guideNpc ?
        // NPC de las guías: misma resolución de ubicación que en misiones, recetas y buscador.
        <NpcCard npcId={guideNpc.id} data-id="element-2813" data-mp="39cd5fhdi6320" /> : locations.length === 0 ? <p className="flex items-start gap-2 rounded-2xl border border-white/10 p-4 text-gray-300" data-id="element-2814" data-mp="39aa9kgpo5fpd">
              <MapPinOff className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-2815" data-mp="34fmq4w9ivbhn" />
              Ubicación no documentada en los archivos disponibles.
            </p> : <div className="grid gap-6 md:grid-cols-2" data-id="element-2816" data-mp="3dc44s9h3hri5">
              {[{
            title: 'Presencia permanente',
            list: permanent
          }, {
            title: 'Solo durante eventos o invasiones',
            list: temporary
          }].map(g => g.list.length > 0 && <div key={g.title} data-id="element-2817" data-mp="4yy9br2hnzec">
                      <h3 className="mb-2 text-sm font-semibold text-gray-400" data-id="element-2818" data-mp="4vslsg9id5kq">{g.title}</h3>
                      <ul className="space-y-2" data-id="element-2819" data-mp="uqgbhozzxab">
                        {g.list.map(l => <li key={l.id} className="flex flex-wrap items-center gap-2" data-id="element-2820" data-mp="jj0zbnqahmzv">
                            <Link to={mapPath(l.mapId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-2821" data-mp="ohuedkl85m25">
                              {mapName(w, l.mapId)}
                            </Link>
                            {l.eventId && <EventChip w={w} eventId={l.eventId} data-id="element-2822" data-mp="tkl5xlolw0z" />}
                          </li>)}
                      </ul>
                    </div>)}
            </div>}
        </WorldSection>

        <WorldSection id="spots" title="Spots y áreas" count={spots.length} data-id="element-2823" data-mp="3s0nf2wswmgew">
          {spots.length ? <ul className="grid gap-2.5" data-id="element-2824" data-mp="3e15rulv9bb7n">{spots.map(s => <SpawnRow key={s.id} w={w} s={s} show="map" data-id="element-2825" data-mp="3e1e7glirkumj" />)}</ul> : <p className="text-gray-400" data-id="element-2826" data-mp="3iyxm3mjdj9f0">No hay spots permanentes configurados para este monstruo.</p>}
        </WorldSection>

        <MonsterEventPositions w={w} monsterId={m.id} data-id="element-2827" data-mp="2ai5k8pwaq3l" />

        {invasionSpawns.length > 0 && <WorldSection id="invasiones" title="Apariciones en invasiones" count={invasionSpawns.length} data-id="element-2828" data-mp="3tgszzm0egsly">
            <p className="mb-3 text-sm text-gray-400" data-id="element-2829" data-mp="3pj53wfmz0tsh">Cada fila es un registro. Las filas de mapas alternativos no son monstruos adicionales.</p>
            <ul className="grid gap-2.5" data-id="element-2830" data-mp="3fje5w3z7bwnn">{invasionSpawns.map(s => <SpawnRow key={s.id} w={w} s={s} show="map" data-id="element-2831" data-mp="3fkz3y3kq5xq9" />)}</ul>
          </WorldSection>}

        {eventIds.length > 0 && <WorldSection id="eventos" title="Eventos e invasiones relacionados" data-id="element-2832" data-mp="35st8ukz6f06u">
            <div className="flex flex-wrap gap-2" data-id="element-2833" data-mp="31rzo20h161i6">{eventIds.map(id => <EventChip key={id} w={w} eventId={id} data-id="element-2834" data-mp="31tkizr79bmgk" />)}</div>
          </WorldSection>}

        <MonsterItemDrops w={w} monsterId={m.id} data-id="element-2835" data-mp="2ws3lm5nzei4q" />

        <BoxContents w={w} bags={monsterBags(w, m.id)} id="recompensas" title="Recompensas posibles al derrotarlo" intro="Lista interna de resultados posibles al derrotar a este monstruo. No es una caja que abras ni se entregan todos juntos." data-id="element-2836" data-mp="2rt0d2b8slts0" />

        <Disclosure title="Estadísticas de catálogo" data-id="element-2837" data-mp="1h95owwpg3x53">
          <StatList rows={[{
          label: 'Nivel (catálogo)',
          value: m.level
        }, {
          label: 'Vida (catálogo)',
          value: m.hp
        }, {
          label: 'Daño mínimo (catálogo)',
          value: m.damageMin
        }, {
          label: 'Daño máximo (catálogo)',
          value: m.damageMax
        }, {
          label: 'Defensa (catálogo)',
          value: m.defense
        }, {
          label: 'Reaparición (catálogo)',
          value: m.respawnSeconds !== undefined ? formatSeconds(m.respawnSeconds) : undefined
        }, {
          label: 'item_rate_divisor',
          value: m.itemRateDivisor,
          note: 'no es una probabilidad de drop'
        }, {
          label: 'ID del monstruo',
          value: m.id
        }]} data-id="element-2838" data-mp="1h7ksf1jkm44l" />
          <p className="mt-3 text-xs text-gray-500" data-id="element-2839" data-mp="9uakskas1mqb">Valores generales del catálogo. Cada spot puede tener estadísticas configuradas distintas; míralas en la ficha del spot.</p>
        </Disclosure>

        <PhotoProvenance w={w} monsterId={m.id} data-id="element-2840" data-mp="1egftfj8eqhqd" />
      </div>
    </div>;
}